// Braze Content Cards utility functions
// This file extends the existing Braze integration with Content Cards functionality

import { reportError } from "./errorReporting";

interface BrazeContentCard {
	id: string;
	viewed: boolean;
	dismissed: boolean;
	created: number;
	updated: number;
	expiresAt?: number;
	url?: string;
	linkText?: string; // Braze Action "Link Text"
	extras?: {
		[key: string]: string;
	};
	title?: string;
	description?: string;
	image?: string;
}

interface ContentCardsResponse {
	cards: BrazeContentCard[];
}

class BrazeContentCardsManager {
	private static instance: BrazeContentCardsManager;
	private cards: BrazeContentCard[] = [];
	private subscribers: Array<(cards: BrazeContentCard[]) => void> = [];
	private isSetup = false;

	private constructor() {
		this.initializeContentCards();
	}

	public static getInstance(): BrazeContentCardsManager {
		if (!BrazeContentCardsManager.instance) {
			BrazeContentCardsManager.instance = new BrazeContentCardsManager();
		}
		return BrazeContentCardsManager.instance;
	}

	private initializeContentCards(): void {
		// Wait for Braze SDK to be available
		this.waitForBrazeAndInitialize();
	}

	private reportBrazeContentCardsError(
		error: unknown,
		action: string,
		metadata: Record<string, unknown> = {},
	): void {
		reportError(error, {
			route:
				typeof window !== "undefined" ? window.location.pathname : undefined,
			component: "BrazeContentCardsManager",
			action,
			metadata,
		});
	}

	private waitForBrazeAndInitialize(): void {
		// Check immediately first (synchronous check)
		if (typeof window !== "undefined" && window.braze) {
			this.setupContentCards();
			return;
		}

		// If not available, start polling with faster interval
		let attempts = 0;
		const maxAttempts = 100; // 5 seconds max (100 * 50ms = faster polling)
		const pollInterval = 50; // Reduced from 100ms to 50ms for faster detection

		const checkBraze = () => {
			attempts++;

			if (typeof window !== "undefined" && window.braze) {
				this.setupContentCards();
			} else if (attempts < maxAttempts) {
				// Retry after a shorter delay for faster detection
				setTimeout(checkBraze, pollInterval);
			} else {
				this.reportBrazeContentCardsError(
					new Error("Braze SDK unavailable for content cards"),
					"wait_for_braze_sdk",
					{
						attempts,
						maxAttempts,
					},
				);
			}
		};

		// Start checking after a very short delay to allow script to load
		setTimeout(checkBraze, pollInterval);

		// Also listen for the braze_ready event (most reliable)
		if (typeof window !== "undefined") {
			window.addEventListener(
				"braze_ready",
				() => {
					this.setupContentCards();
				},
				{ once: true },
			); // Use once: true to avoid memory leaks
		}
	}

	private setupContentCards(): void {
		if (typeof window === "undefined" || !window.braze) {
			return;
		}

		// Prevent multiple setups
		if (this.isSetup) {
			return;
		}
		this.isSetup = true;

		try {
			// Get cached Content Cards FIRST (synchronous, immediate)
			// This allows subscribers to get cached data right away
			const cachedCards = window.braze.getCachedContentCards();
			if (cachedCards?.cards && cachedCards.cards.length > 0) {
				this.cards = cachedCards.cards as unknown as BrazeContentCard[];
				this.notifySubscribers(); // Notify immediately with cached cards
			}

			// Subscribe to Content Cards updates (for fresh cards)
			window.braze.subscribeToContentCardsUpdates((response: any) => {
				this.cards = response.cards || [];
				this.notifySubscribers();
			});

			// Request fresh Content Cards from server (async, non-blocking)
			window.braze.requestContentCardsRefresh();
		} catch (error) {
			this.reportBrazeContentCardsError(error, "setup_content_cards");
			this.isSetup = false; // Reset on error so we can retry
		}
	}

	private notifySubscribers(): void {
		for (const callback of this.subscribers) {
			callback(this.cards);
		}
	}

	public subscribe(callback: (cards: BrazeContentCard[]) => void): () => void {
		this.subscribers.push(callback);

		// If Braze is already available, check cached cards immediately before calling callback
		// This ensures subscribers get cached cards right away if available
		if (
			typeof window !== "undefined" &&
			window.braze &&
			this.cards.length === 0
		) {
			try {
				const cachedCards = window.braze.getCachedContentCards();
				if (cachedCards?.cards && cachedCards.cards.length > 0) {
					this.cards = cachedCards.cards as unknown as BrazeContentCard[];
				}
			} catch (error) {
				this.reportBrazeContentCardsError(error, "read_cached_cards");
			}
		}

		// Immediately call with current cards (may include cached cards now)
		callback(this.cards);

		// Return unsubscribe function
		return () => {
			const index = this.subscribers.indexOf(callback);
			if (index > -1) {
				this.subscribers.splice(index, 1);
			}
		};
	}

	public getCards(): BrazeContentCard[] {
		return [...this.cards];
	}

	public getCardsByType(contentCardType: string): BrazeContentCard[] {
		return this.cards.filter(
			(card) => card.extras?.content_card_type === contentCardType,
		);
	}

	public getCardsForPage(pageName: string): BrazeContentCard[] {
		return this.cards.filter((card) => {
			const pageNames = card.extras?.page_names;
			if (!pageNames) return false;

			// Split by comma and check if pageName is included
			const pages = pageNames.split(",").map((p) => p.trim());
			return pages.includes(pageName);
		});
	}

	public getCardsWithPromoCode(): BrazeContentCard[] {
		return this.cards.filter(
			(card) => card.extras?.promo_code && card.extras.promo_code.trim() !== "",
		);
	}

	public getCardsWithFirstName(): BrazeContentCard[] {
		return this.cards.filter(
			(card) => card.extras?.first_name && card.extras.first_name.trim() !== "",
		);
	}

	public getUnviewedCards(): BrazeContentCard[] {
		return this.cards.filter((card) => !card.viewed);
	}

	public getUnviewedCount(): number {
		return this.cards.filter((card) => !card.viewed).length;
	}

	public logCardClick(cardId: string): void {
		if (window.braze) {
			if (typeof window.braze.logContentCardClick === "function") {
				const card = this.cards.find((c) => c.id === cardId);

				if (card && !card.url) {
					card.url = "no_url";
				}

				if (card) {
					window.braze.logContentCardClick(card as any);
				}
			}
		}
	}

	public logCardDismissal(cardId: string): void {
		if (window.braze) {
			// Try the correct Braze SDK method for logging dismissals
			if (typeof (window.braze as any).logContentCardDismissed === "function") {
				(window.braze as any).logContentCardDismissed(cardId);
			} else if (typeof window.braze.logCardDismissal === "function") {
				window.braze.logCardDismissal(cardId as any);
			}
		}
	}

	public logCardImpression(cardId: string): void {
		if (window.braze) {
			if (typeof window.braze.logContentCardImpressions === "function") {
				const card = this.cards.find((c) => c.id === cardId);
				if (card) {
					window.braze.logContentCardImpressions([card as any]);
				}
			}
		}
	}

	public refreshCards(): void {
		if (window.braze) {
			window.braze.requestContentCardsRefresh();
		}
	}

	public forceInitialize(): void {
		this.setupContentCards();
	}

	public isBrazeAvailable(): boolean {
		return typeof window !== "undefined" && !!window.braze;
	}

	// Method to examine specific keys you mentioned
	public examineCardKeys(): {
		content_card_type: string[];
		page_names: string[];
		promo_code: string[];
		first_name: string[];
	} {
		const result = {
			content_card_type: [] as string[],
			page_names: [] as string[],
			promo_code: [] as string[],
			first_name: [] as string[],
		};

		for (const card of this.cards) {
			if (card.extras) {
				if (card.extras.content_card_type) {
					result.content_card_type.push(card.extras.content_card_type);
				}
				if (card.extras.page_names) {
					result.page_names.push(card.extras.page_names);
				}
				if (card.extras.promo_code) {
					result.promo_code.push(card.extras.promo_code);
				}
				if (card.extras.first_name) {
					result.first_name.push(card.extras.first_name);
				}
			}
		}

		return result;
	}

	// Method to get detailed information about cards with your specific keys
	public getCardsWithSpecificKeys(): Array<{
		card: BrazeContentCard;
		content_card_type?: string;
		page_names?: string;
		promo_code?: string;
		first_name?: string;
	}> {
		return this.cards.map((card) => ({
			card,
			content_card_type: card.extras?.content_card_type,
			page_names: card.extras?.page_names,
			promo_code: card.extras?.promo_code,
			first_name: card.extras?.first_name,
		}));
	}
}

// Export singleton instance
export const brazeContentCardsManager = BrazeContentCardsManager.getInstance();

// Export types for use in other components
export type { BrazeContentCard, ContentCardsResponse };
